import React, { Fragment, FunctionComponent, useState } from 'react';
import classNames from 'classnames';

import { Button, Heading, TextInput, Typography } from '@savebetter/components';
import { exponea } from '@savebetter/tracking-events';

import { useMediaPercentageProps } from '../hooks/useMediaPercentageProps';

import { MediaProps } from './Media';
import { MediaBlockContentProps } from './MediaBlockContent';
import { PageSectionBlok, PageSectionProps } from './PageSection';
import { PageSectionHeader } from './PageSectionHeader';
import { RichText } from './RichText';
import { Section } from './Section';

interface Blok extends PageSectionBlok {
  reference: {
    content: {
      global: {
        title?: string;
        media: MediaProps[];
        description?: string;
        content: MediaBlockContentProps[];
        isImageLeft?: boolean;
        cancellationPolicy?: string;
        hasPaddingBottom: boolean;
        hasPaddingTop: boolean;
        hasBackground: boolean;
        hasBorder: boolean;
      }[];
    };
  };
}

interface NewsletterProps extends PageSectionProps {
  blok: Blok;
}

export const Newsletter: FunctionComponent<React.PropsWithChildren<NewsletterProps>> = ({ blok, sectionClassName }) => {
  const refBlok = blok.reference.content?.global[0];
  const [email, setEmail] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const {
    hasBackground,
    hasBorder,
    title,
    content,
    media,
    hasPaddingBottom,
    hasPaddingTop,
    isImageLeft,
    cancellationPolicy
  } = refBlok;
  const { getMediaProps } = useMediaPercentageProps();
  const mediaProps = getMediaProps(media[0]);
  const submit = () => {
    exponea.identifyUser({ email: email.toLowerCase().trim() });
    exponea.trackEvent({
      eventName: 'consent',
      restTrackingObject: {
        action: 'accept',
        timestamp: Math.round(Date.now() / 1000),
        email: email.toLowerCase().trim(),
        source: 'newsletter component raisin us',
        category: 'savings',
        valid_until: 'unlimited',
        placement: 'NEWSLETTER_COMPONENT'
      },
      successCallback: () => {
        setIsSubmitted(true);
      },
      errorCallback: () => {
        setIsSubmitted(true);
        setIsError(true);
      }
    });
  };

  return (
    <Section
      width="md"
      hasBackground={hasBackground}
      hasBorder={hasBorder}
      className={sectionClassName}
      blok={blok}
      hasPaddingBottom={hasPaddingBottom}
      hasPaddingTop={hasPaddingTop}
    >
      {isSubmitted ? (
        <>
          <PageSectionHeader>
            {isError ? 'Your subscription request failed' : 'Thank you for subscribing!'}
          </PageSectionHeader>
          <Typography variant="body" className="text-center">
            {isError
              ? 'Please try again'
              : `You’re now on the list to receive news and updates from Raisin. 
              Keep your eyes out for a confirmation in your inbox shortly.`}
          </Typography>
          <Button kind="secondary" className="mx-auto mt-8" onClick={() => setIsSubmitted(false)}>
            Return to Raisin
          </Button>
        </>
      ) : (
        <div className={classNames('flex flex-col md:flex-row', { 'md:flex-row-reverse': isImageLeft })}>
          <div className="md:w-2/4 flex">
            {media && <img {...mediaProps} alt="newsletter" className="w-3/5 md:w-2/3 m-auto" />}
          </div>
          <div className="text-center md:text-start my-auto md:w-2/4">
            {content &&
              content.map(({ _uid, heading, content }) => (
                <Fragment key={_uid}>
                  <Heading
                    className="mt-large md:first:mt-0 lg:mt-large lg:first:mt-0 mb-4"
                    kind="3"
                    color="text-black"
                    level={title ? '3' : '2'}
                  >
                    {title}
                  </Heading>
                  <RichText content={content} className={heading ? 'mt-4' : ''} />
                  <div className="mt-3 md:mt-0 flex flex-col f md:flex-row items-center">
                    <TextInput
                      error={false}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="Your email here"
                      wrapperBorderColor="border-black-40"
                      wrapperClassName="w-full md:w-3/4"
                    />
                    <Button kind="primary" className="mt-8 md:ml-4 md:mt-2 md:float-right" onClick={submit}>
                      Subscribe
                    </Button>
                  </div>
                  <Typography variant="subtitle2" className="mt-3 flex">
                    Cancellation policy:&nbsp; <RichText content={cancellationPolicy} />
                  </Typography>
                </Fragment>
              ))}
          </div>
        </div>
      )}
    </Section>
  );
};
